/**
 * LAGET IDAG — prototypens data-screen="var", "vem är var".
 *
 * DAGEN KOMMER UR SERVERN. TODAY bär request_in 'none': klienten skickar inget
 * datum, servern läser dagen i Europe/Stockholm och sänder tillbaka den. Skärmen
 * visar DEN dagen och aldrig sin egen idag() — annars kunde en telefon med fel
 * tidszon påstå att laget gäller i går.
 *
 * "RAPPORTERAT" ÄR INTE INCHECKNING (beslut 06). Klartid har varken stoppur
 * eller incheckning; listan visar vem som har MINST EN levande tidrad i dag,
 * oavsett vem som skrev in den — chefen som fyllt i åt någon får honom alltså
 * att synas här. Och ingen rad på den här skärmen gör någon dyrare: beslut 18
 * räknar UPPLAGDA personer, inte rader.
 *
 * BOKFÖRAREN SER SKÄRMEN. TODAY är öppen för FULL_VIEW_ROLES, alltså manager OCH
 * bookkeeper (beslut 07) — men skärmen har inga knappar att stänga av för honom,
 * för det finns ingenting att göra här.
 *
 * PROTOTYPENS "Tallmovägen 22" UNDER NAMNET GÅR INTE ATT BYGGA, och inte heller
 * knappen "Ring": TodayResponse bär account_id, namn, minuter och frånvaro — inte
 * vilken arbetsorder någon står på och inget telefonnummer. Se LUCKOR.
 */

import { useEffect, useState } from 'react';
import { TODAY } from '@delat/routes';
import type { AbsenceKind, TodayResponse } from '@delat/types';
import { Datumrad, Felruta, Knapp, Laddar, Personrad, Tomtext, Yta, initialer } from '../ui';
import { klienten } from '../api';
import type { Klartidfel } from '../api';
import { granskaIdag } from '../api/granskare/lagetidag';
import { useInloggad } from '../app/session';
import { visaDag, visaMinuter } from '../app/datum';

const FRANVAROTEXT: Record<AbsenceKind, string> = {
  vacation: 'Semester',
  leave: 'Ledig',
  other: 'Annan frånvaro',
};

type Lage =
  | { readonly status: 'laddar' }
  | { readonly status: 'fel'; readonly fel: Klartidfel }
  | { readonly status: 'klart'; readonly svar: TodayResponse };

export const LagetIdag = () => {
  const { firmaId } = useInloggad();
  const [lage, sattLage] = useState<Lage>({ status: 'laddar' });
  const [omgang, sattOmgang] = useState(0);

  useEffect(() => {
    const styrning = new AbortController();
    let vaken = true;
    sattLage({ status: 'laddar' });

    void (async () => {
      const svar = await klienten.anrop(
        TODAY,
        { params: { company_id: firmaId }, signal: styrning.signal },
        granskaIdag,
      );
      if (!vaken) return;
      if (!svar.ok) {
        if (svar.fel.slag === 'nat' && svar.fel.avbrutet) return;
        sattLage({ status: 'fel', fel: svar.fel });
        return;
      }
      sattLage({ status: 'klart', svar: svar.varde });
    })();

    return () => {
      vaken = false;
      styrning.abort();
    };
  }, [firmaId, omgang]);

  if (lage.status === 'laddar') return <Laddar rader={5} />;
  if (lage.status === 'fel') {
    return (
      <Felruta
        fel={lage.fel}
        atgard={
          <Knapp variant="piller" onClick={() => sattOmgang((n) => n + 1)}>
            Försök igen
          </Knapp>
        }
      />
    );
  }

  const { work_date, reported, not_reported } = lage.svar;

  return (
    <>
      <Datumrad stark={visaDag(work_date)}>vem har rapporterat</Datumrad>

      <Yta rubrik="Rapporterat i dag" rakning={`${reported.length} st`}>
        {reported.map((rad) => (
          <Personrad
            key={rad.account_id}
            initialer={initialer(rad.name)}
            namn={rad.name}
            varde={visaMinuter(rad.minutes)}
          />
        ))}
        {reported.length === 0 && <Tomtext>Ingen har skrivit in någon tid än i dag.</Tomtext>}
      </Yta>

      <Yta rubrik="Har inte rapporterat" rakning={`${not_reported.length} st`}>
        {not_reported.map((rad) => (
          <Personrad
            key={rad.account_id}
            initialer={initialer(rad.name)}
            namn={rad.name}
            under={rad.absence === null ? 'Ingen tid i dag' : FRANVAROTEXT[rad.absence]}
          />
        ))}
        {not_reported.length === 0 && <Tomtext>Alla har rapporterat.</Tomtext>}
      </Yta>

      <Tomtext>
        Det här är rapportering, inte närvaro. Klartid har varken stoppur eller
        incheckning — den som skrivit in sin dag står ovan, även om chefen fyllde i
        åt honom.
      </Tomtext>
    </>
  );
};
